import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Gauge, Zap, MapPin, Clock, Timer, Car, ExternalLink, Navigation, MessageSquare, Video, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { formatDT, formatSpeed, resolveTimezone } from '../../utils/timezone';
import { getTimezoneLabel } from '../../utils/timezoneList';
import { formatRecordDeviceLabel } from '../../utils/deviceLabel';
import { scanAlertQuickAction } from '../../utils/scanAlertLink';

const EventDetailModal = ({ event, open, onOpenChange, onViewDevice, devicesById = null }) => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { isSubpageEnabled } = useFeatureFlags();
  if (!event) return null;

  const isSpeed = event.event_type?.includes('overspeed');
  const isVideoEvent = event.event_type === 'video_event';
  const canViewEventVideos = isVideoEvent && isSubpageEnabled('media_event_videos');
  const Icon = isSpeed ? Gauge : Zap;
  const eventTz = event.device_timezone || resolveTimezone();
  const tzLabel = getTimezoneLabel(eventTz) || eventTz;
  const hasLocation = event.latitude != null && event.longitude != null;
  const mapsUrl = hasLocation ? `https://maps.google.com/?q=${event.latitude},${event.longitude}` : null;

  const severityColor = event.severity === 'critical'
    ? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
    : event.severity === 'warning'
    ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
    : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400';

  const statusColor = event.status === 'active'
    ? 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400'
    : event.status === 'cleared'
    ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400'
    : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400';

  const severityIconBg = event.severity === 'critical'
    ? 'bg-red-100 dark:bg-red-900/40'
    : event.severity === 'warning'
    ? 'bg-amber-100 dark:bg-amber-900/40'
    : 'bg-slate-100 dark:bg-slate-800';

  const severityIconColor = event.severity === 'critical'
    ? 'text-red-600'
    : event.severity === 'warning'
    ? 'text-amber-600'
    : 'text-slate-600';

  const formatDuration = (secs) => {
    if (secs == null) return null;
    if (secs >= 3600) return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m ${Math.round(secs % 60)}s`;
    if (secs >= 60) return `${Math.floor(secs / 60)}m ${Math.round(secs % 60)}s`;
    return `${Math.round(secs)}s`;
  };

  const openEventVideo = async () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: eventTz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(new Date(event.triggered_at));
    const get = (t) => parts.find(p => p.type === t)?.value || '00';
    const hh = get('hour') === '24' ? '00' : get('hour');
    const date = `${get('year')}-${get('month')}-${get('day')}`;
    let videoParam = '';
    try {
      const code = event.metadata?.trigger_value;
      const res = await api.get('/media/event-videos-match', {
        params: { device_id: event.device_id, triggered_at: event.triggered_at, ...(code ? { event_code: code } : {}) },
      });
      if (res.data?.video_id) {
        const ids = (res.data.matches || []).map(m => m.video_id).filter(Boolean);
        videoParam = `&video_id=${ids.length > 0 ? ids.join(',') : res.data.video_id}`;
      } else {
        toast.info('Exact clip not found — showing videos around the event time');
      }
    } catch (e) {
      console.error('Event video match lookup failed:', e);
    }
    onOpenChange(false);
    navigate(`/media?tab=events&device_id=${event.device_id}&date=${date}&time=${hh}:${get('minute')}:${get('second')}${videoParam}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="event-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${severityIconBg}`}>
              <Icon className={`w-5 h-5 ${severityIconColor}`} />
            </div>
            <div>
              <div className="text-base font-bold">{event.event_rule_name}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${severityColor}`}>{event.severity}</Badge>
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 capitalize ${statusColor}`}>{event.status}</Badge>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Device Info */}
          <div className="p-3 rounded-lg bg-muted/50 border space-y-2">
            <div className="flex items-center gap-2">
              <Car className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-semibold" data-testid="event-modal-device-label">{formatRecordDeviceLabel(event, devicesById)}</span>
            </div>
            {event.driver_name && (
              <div className="text-xs text-muted-foreground pl-6">Driver: {event.driver_name}</div>
            )}
          </div>

          {/* Notification Message */}
          {event.message && (
            <div className="flex items-start gap-2 p-3 rounded-lg border bg-muted/30" data-testid="event-modal-notification-message">
              <MessageSquare className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Notification Message</div>
                <div className="text-sm font-medium">{event.message}</div>
              </div>
            </div>
          )}

          {/* Speed Info */}
          {isSpeed && (event.speed_kmh != null || event.threshold_kmh != null) && (
            <div className="p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 space-y-1">
              <div className="text-sm font-semibold text-red-700 dark:text-red-400">
                {event.speed_kmh != null ? formatSpeed(event.speed_kmh) : ''}{event.threshold_kmh != null ? ` (Limit: ${formatSpeed(event.threshold_kmh)})` : ''}
              </div>
              {event.road_name && (
                <div className="text-xs text-red-600 dark:text-red-400">on {event.road_name}</div>
              )}
            </div>
          )}

          {/* Timing */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div>
                <div className="text-xs text-muted-foreground">Triggered</div>
                <div className="font-medium">{formatDT(event.triggered_at, { timezone: eventTz, seconds: true })} &mdash; {tzLabel}</div>
              </div>
            </div>
            {event.cleared_at && (
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-green-600 flex-shrink-0" />
                <div>
                  <div className="text-xs text-muted-foreground">Cleared</div>
                  <div className="font-medium">{formatDT(event.cleared_at, { timezone: eventTz, seconds: true })} &mdash; {tzLabel}</div>
                </div>
              </div>
            )}
            {event.duration_secs != null && (
              <div className="flex items-center gap-2 text-sm">
                <Timer className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div>
                  <div className="text-xs text-muted-foreground">Duration</div>
                  <div className="font-medium">{formatDuration(event.duration_secs)}</div>
                </div>
              </div>
            )}
          </div>

          {/* Location */}
          {hasLocation && (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30 hover:bg-muted/60 transition-colors"
              data-testid="event-detail-map-link"
            >
              <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-xs text-muted-foreground">Event Location</div>
                <div className="text-sm text-blue-500 font-medium">{Number(event.latitude).toFixed(5)}, {Number(event.longitude).toFixed(5)}</div>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
            </a>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t">
            {(() => {
              const scanAction = scanAlertQuickAction(event.event_type, event.metadata?.credential);
              if (!scanAction) return null;
              return (
                <Button
                  className="flex-1 bg-[#0891b2] hover:bg-[#0e7490]"
                  onClick={() => { onOpenChange(false); navigate(scanAction.url); }}
                  data-testid="event-detail-scan-quick-action"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {scanAction.label}
                </Button>
              );
            })()}
            {canViewEventVideos && (
              <Button
                className="flex-1 bg-[#7c3aed] hover:bg-[#6d28d9]"
                onClick={openEventVideo}
                data-testid="event-detail-view-video-event"
              >
                <Video className="w-4 h-4 mr-2" />
                View Video Event
              </Button>
            )}
            {onViewDevice && hasLocation && (
              <Button
                className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8]"
                onClick={() => { onViewDevice(event.device_id, event.latitude, event.longitude, event.id); onOpenChange(false); }}
                data-testid="event-detail-view-event-map"
              >
                <Navigation className="w-4 h-4 mr-2" />
                View Event on Map
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EventDetailModal;
